import { useEffect, useState } from "react";
import { type IUpload } from "../../libs/dragdrop/DragAndDrop";
import { FMFile } from "../../shared/FMFile";
import Dirs, { GDir } from "../../fmanlibs/Dirs";
import Utils from "../../libs/Utils";
import Files from "../../fmanlibs/Files";
import { TConfligResolution } from "../../fmanlibs/FManApi";
import Button from "../../gui/Button/Button";
import Loading from "../../gui/Loading/Loading";
import CheckFilled from "../../gui/Icons/CheckFilled";
import IconButton from "../../gui/Button/IconButton";
import CloseIcon from "../../gui/Icons/CloseIcon";
import { useOverlay } from "../../gui/Overlay/OverlayProvider";

export interface IUploadProps {
    root:FMFile
    files:IUpload[]
}

const MAX_FILE_SIZE = 100_000_000
const CHUNK = 1024*100
const DIALOG_ID = "uploads"

async function loadFile(file:File) : Promise<Uint8Array>{
    return new Promise((success,fail) => {
        const reader = new FileReader()
        reader.onload = (e:any) => success(new Uint8Array(e.target.result))
        reader.onerror = fail
        reader.readAsArrayBuffer(file)
    })
}

function Upload(props:{root:FMFile, file:IUpload, onComplete:Function}) {
    const {file, onComplete} = props
    const [progress,setProgress] = useState<number>(0)
    const [root,setRoot] = useState<GDir|null>(null)
    const [error,setError] = useState<string>("")
    const [completed,setCompleted] = useState<boolean>(false)
    const [isDuplicate,setIsDuplicate] = useState<boolean>(false)

    const loadRoot = async () => {
        const rootDir = await Dirs.i.findPath(props.root._id, file.path)
        if (rootDir) {
            setRoot(rootDir)
            return rootDir
        }
        else throw `Path ${file.path} not found`
    }
    const upload = async (root:GDir, conflictResolution:TConfligResolution="none") => {
        const size = file.file.size
        let partial : FMFile
        try {
            setError("")
            setIsDuplicate(false)

            partial = await Files.i.createSession(root._id, file.file.name, size, conflictResolution)
            partial.parentId && Files.i.expire(partial.parentId).get(partial.parentId)
        }catch(e){
            if (Utils.httpStatus(e)==409) {
                setError("File already exits.")
                return setIsDuplicate(true)
            }
            else {
                return setError(Utils.errorMsg(e).message)
            }
        }

        try {
            const buffer = await loadFile(file.file)
            let offset = 0
            while(offset<size){
                const end = Math.min(offset+CHUNK, size)
                await Files.i.appendFile(partial._id, offset, buffer.slice(offset, end))
                offset += CHUNK
                setProgress(offset/size*100)
            }
            setCompleted(true)
        }catch(e){
            setError(Utils.errorMsg(e).message)
        }finally {
            onComplete()
        }
    }

    const onCopy = () => upload(root!, "rename")
    const onReplace = () => upload(root!, "replace")
    const onCancel = () => { setError("Cancelled"); onComplete() }

    useEffect(() => {loadRoot().then(upload).catch(e => setError(Utils.errorMsg(e).message))}, [])

    const style = {width:progress+"%"}
    const progressbar = root && !completed ? <div className="progress"><div className="bar" style={style}/></div> : null
    const conflict = isDuplicate ? <>
        <Button variant="text" onClick={onCopy}>Keep Both</Button>
        <Button variant="text" onClick={onReplace}>Replace</Button>
    </> : null
    const err = error ? <div className="error">{error}</div> : null
    return <div className="upload">
        <div className="flex middle gap-8">
            <div className="flex1">{root?.path}/{file.file.name}</div>
            {!root ? <Loading/> : null}
            {conflict}
            {completed ? <CheckFilled/> : <IconButton size="small" onClick={onCancel}><CloseIcon/></IconButton>}
        </div>
        {progressbar}{err}
    </div>
}

export function UploadDlg(props:IUploadProps) {
    const [count,setCount] = useState<number>(0)
    const {popOverlay} = useOverlay()
    const onCancel = () => popOverlay(DIALOG_ID)
    const onComplete = () => setCount(c => {
        const total = c+1
        if (total==props.files.length) setTimeout(onCancel, 100)
        return total
    })
    const uploads = props.files.map(file => <Upload root={props.root} file={file} key={file.path+"/"+file.file.name} onComplete={onComplete}/>)
    return <>
        <h2>Uploading {count+1}/{props.files.length}...</h2>
        <div className="content">
            {uploads}
        </div>
        <div className="actions">
            <Button size="medium" color="primary" onClick={onCancel}>Cancel</Button>
        </div>
    </>
}

export default function useUploadDlg(){
    const {addOverlay,hasOverlay} = useOverlay()
    const [busy,setBusy] = useState<boolean>(false)
    const showUploads = (root:FMFile, files:IUpload[]) => {
        const toUpload = files.filter(file => file.file.size<=MAX_FILE_SIZE)
        if (toUpload.length==0 || hasOverlay(DIALOG_ID)) return setBusy(false)
        else setBusy(true)
        addOverlay(DIALOG_ID, {children:<UploadDlg root={root} files={toUpload}/>, className:"dialog", modal:true, afterClosed:()=>setBusy(false)})        
    }
    return {showUploads,busy,setBusy}
}